import React from "react"
import { Modal, Form, Col, Row, Button } from "react-bootstrap"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { APIError } from "../../api/ObservableFromFetch"
import { GetRoleOrderSecurity, SetRoleOrderSecurity, OrderViewSecurity, OrderSecurityException } from "../../api/Roles/RoleOrderSecurityProcess"
import { AppState } from "../../AppState"
import { RoleIdForSelectedCompany, getUserNameForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import ActionButton from "../ActionButton"
import DualListBox from "../Administration/DualListBox"

interface OrderSecurityAuthProps {
  PrimaryRoleID?: number
  UserName?: string
}

interface OrderSecurityProps {
  companyName: string
  currentURL?: string
  invitationMode?: boolean
  override?: boolean
  cancelText: string
  submitText: string
  cancelVariant?: string
  onPrev: () => void
  onNext: () => void
}

interface OrderSecurityState {
  loading: boolean
  saving: boolean
  overrideOrderSecurity?: boolean
  showOrderSecurityForm: boolean
  roles: OrderSecurityException[]
  selection: OrderSecurityException[]
  selectionLeft: string[]
  selectionRight: string[]
}

export class OrderSecurity extends React.PureComponent<OrderSecurityProps & OrderSecurityAuthProps, OrderSecurityState> {
  userRolesSubscription?: Subscription
  updateSubscription?: Subscription
  state = { loading: false, saving: false, overrideOrderSecurity: this.props.override, showOrderSecurityForm: false, roles: [], selection: [], selectionLeft: [], selectionRight: [] }

  componentDidMount() {
    if (this.props.override) {
      this.showSecurityForm()
    }
  }

  componentWillUnmount() {
    this.userRolesSubscription && this.userRolesSubscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
  }

  onCancel = () => {
    if (this.state.overrideOrderSecurity) {
      this.setState({ selection: [], selectionLeft: [], selectionRight: [] })
    }
    this.props.onPrev()
  }

  onSubmit = () => {
    if (this.state.showOrderSecurityForm) {
      this.updateOrderSecurity()
      return
    }
    if (this.state.overrideOrderSecurity) {
      this.showSecurityForm()
    } else {
      this.props.onNext()
    }
  }

  showSecurityForm() {
    const primaryRoleID = this.props.PrimaryRoleID || 0
    this.setState({ showOrderSecurityForm: true, loading: true })
    this.userRolesSubscription = GetRoleOrderSecurity(primaryRoleID, this.props.currentURL).subscribe(
      (data: OrderViewSecurity) => {
        this.setState({
          roles: [...data.CanOnlySeeOrdersTheyHavePlacedRole, ...data.CanSeeOrdersAnyUserHasPlacedRole],
          selection: data.CanOnlySeeOrdersTheyHavePlacedRole,
          loading: false
        })
      },
      error => {
        toast.error(error)
        this.setState({ loading: false })
      }
    )
  }

  updateOrderSecurity() {
    this.setState({ saving: true })
    const roles: OrderSecurityException[] = this.state.roles
    const selection: OrderSecurityException[] = this.state.selection
    const canSeeOrdersAnyUserHasPlacedRoles = roles.filter((role: OrderSecurityException) => !selection.includes(role))
    const canOnlySeeOrdersTheyHavePlacedRoles = roles.filter((role: OrderSecurityException) => selection.includes(role))
    const data = {
      CanSeeOrdersAnyUserHasPlacedRole: canSeeOrdersAnyUserHasPlacedRoles,
      CanOnlySeeOrdersTheyHavePlacedRole: canOnlySeeOrdersTheyHavePlacedRoles,
      ModifiedBy: this.props.UserName || "",
      Link: this.props.currentURL || ""
    }
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.updateSubscription = SetRoleOrderSecurity(data).subscribe(
      (data) => {
        this.setState({ saving: false })
        this.props.onNext()
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  render() {
    const { showOrderSecurityForm, loading, roles, selection } = this.state
    const content = <>
      <Modal.Header>
        <Modal.Title className="text-uppercase">Order Security</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!showOrderSecurityForm && <React.Fragment>
          <Row><Col><p>Do you want certain user roles to be able to view Orders placed by other users?</p></Col></Row>
          <Row className="d-flex justify-content-center">
            <Col md="auto">
              <Form.Check type="radio" name="securityOption" id="optionYes" value="1" label="Yes" className="abc-radio" onClick={() => this.setState({ overrideOrderSecurity: true })} />
            </Col>
            <Col md="auto">
              <Form.Check type="radio" name="securityOption" id="optionNo" value="0" label="No" className="abc-radio" onClick={() => this.setState({ overrideOrderSecurity: false })} />
            </Col>
          </Row>
        </React.Fragment>}
        {showOrderSecurityForm && <React.Fragment>
          <Form className="px-4">
            <Form.Group as={Row}>
              <Form.Label column="sm" sm={3} className="text-lg-right">Company</Form.Label>
              <Col md={9}>
                <Form.Control type="text" size="sm" defaultValue={this.props.companyName} disabled></Form.Control>
              </Col>
            </Form.Group>
            <DualListBox
              disabled={loading}
              options={roles}
              selected={selection}
              idKey="RoleID"
              labelKey="Title"
              availableTitle="Can see Orders any user has placed"
              selectedTitle="Can only see Orders they have placed"
              onChange={selected => this.setState({ selection: [...selected] })}
            />
          </Form>
        </React.Fragment>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={this.props.cancelVariant} onClick={this.onCancel} className="px-4">{this.props.cancelText}</Button>
        <ActionButton variant="primary" disabled={this.state.overrideOrderSecurity === undefined} onClick={this.onSubmit} className="px-4" spin={this.state.saving} disableOnSpin>{this.props.submitText}</ActionButton>
      </Modal.Footer>
    </>

    return <>
      {this.props.invitationMode && <Modal.Dialog size="xl">{content}</Modal.Dialog>}
      {!this.props.invitationMode && <Modal backdrop="static" show size="xl">{content}</Modal>}
    </>
  }

  static defaultProps = { cancelText: "Previous", submitText: "Next" }
}

function mapStateToProps(state: AppState): OrderSecurityAuthProps {
  return {
    PrimaryRoleID: RoleIdForSelectedCompany(state),
    UserName: getUserNameForSelectedCompany(state)
  }
}
const ConnectedOrderSecurity = connect(mapStateToProps, null)(OrderSecurity)
export default ConnectedOrderSecurity